import { FC, Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Models
import IUser from 'app/models/User';
import IContact from 'app/models/Contact';
// Selectors
import { selectHcpcsCodesEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectTeamsAsOptions } from 'app/store/Teams/Teams.selectors';
import { selectAccountsAsOptions } from 'app/store/Clients/Clients.selectors';
// Mui
import { Box } from '@mui/material';
// Components
import { OutlineBlock, InfoBlock } from 'app/components/Utilities';
// Utilities
import { convertDateToString, convertTimeToString } from 'app/utilities/DateTime';

interface IFormData {
  name: string;
  teamId: string | number;
  clientAccountId?: string | number;
  claimant: {
    name: string;
    dateOfBirth: Dayjs | null;
  },
  settings: {
    medicareOverchargeThreshold: number;
  };
  eventDate: Dayjs | null,
  eventTime: Dayjs | null;
  claimNumber: string;
  notificationPreferences: {
    recipientIds: IUser[];
  };
  type: string;
  summary: string;
  expectedOutcome: string;
  contacts: {
    contact: IContact | null;
    role: string;
  }[];
};

const ReviewCreateStepContent:FC = () => {
  const { t } = useTranslation();

  // State
  const hcpcsCodesEnabled = useAppSelector(selectHcpcsCodesEnabled);
  const teamOptions = useAppSelector(selectTeamsAsOptions);
  const accountOptions = useAppSelector(selectAccountsAsOptions);

  const { getValues } = useFormContext<IFormData>();

  const formData = getValues();

  const teamName = useMemo(() => {
    if ( !teamOptions ) return '';
    const foundedOption = teamOptions.find(({ value }) => value === Number(formData.teamId));
    return foundedOption?.label || '-';
    // eslint-disable-next-line
  }, [teamOptions, formData.teamId]);

  const clientAccountName = useMemo(() => {
    if ( !accountOptions ) return '';
    const foundedOption = accountOptions.find(({ value }) => value === Number(formData.clientAccountId));
    return foundedOption?.label || '-';
    // eslint-disable-next-line
  }, [accountOptions, formData.clientAccountId]);

  const medicareOverchargeThreshold = useMemo(() => {
    if ( !hcpcsCodesEnabled ) return undefined;
    return formData.settings.medicareOverchargeThreshold || '-';
    // eslint-disable-next-line
  }, [formData.settings.medicareOverchargeThreshold]);

  const eventDate = formData.eventDate ? convertDateToString(formData.eventDate) as string : '-';
  const eventTime = formData.eventTime ? convertTimeToString(formData.eventTime) as string : '-';
  const claimName = formData.claimant.name || '-';
  const dateOfBirth = formData.claimant.dateOfBirth ? convertDateToString(formData.claimant.dateOfBirth) as string : '-';
  const claimNumber = formData.claimNumber || '-';

  return (
    <Box sx={{ pt: 2 }}>
      <OutlineBlock label={t('labels.general')}>
        <Fragment>
          <InfoBlock label={t('labels.caseName')} value={formData.name || '-'} direction="row" />
          <InfoBlock label={t('labels.type')} value={formData.type || '-'} direction="row" />
          <InfoBlock label={t('labels.eventDate')} value={eventDate} direction="row" />
          {formData.eventTime ? (
            <InfoBlock label={t('labels.eventTime')} value={eventTime} direction="row" />
          ) : null}
          <InfoBlock label={t('labels.team')} value={teamName} direction="row" />
          <InfoBlock label={t('labels.clientAccount')} value={clientAccountName} direction="row" />
        </Fragment>
      </OutlineBlock>
      <OutlineBlock sx={{ mt: 6 }} label={t('labels.claimant')}>
        <Fragment>
          <InfoBlock label={t('labels.name')} value={claimName} direction="row" />
          <InfoBlock label={t('labels.dateOfBirth')} value={dateOfBirth} direction="row" />
          {claimNumber ? (
            <InfoBlock label={t('labels.claimNumber')} value={claimNumber} direction="row" />
          ) : null}
        </Fragment>
      </OutlineBlock>
      <OutlineBlock sx={{ mt: 6 }} label={t('labels.details')}>
        <Fragment>
          <InfoBlock label={t('labels.summary')} value={formData.summary || '-'} />
          <InfoBlock label={t('labels.expectedOutcome')} value={formData.expectedOutcome || '-'} />
        </Fragment>
      </OutlineBlock>
      {typeof medicareOverchargeThreshold !== 'undefined' ? (
        <OutlineBlock sx={{ mt: 6 }} label={t('labels.medicare')}>
          <Fragment>
            <InfoBlock label={t('labels.medicareOverchargeThreshold')} value={medicareOverchargeThreshold} direction="row" />
          </Fragment>
        </OutlineBlock>
      ) : null}
      <OutlineBlock sx={{ mt: 6 }} label={t('labels.notifications')}>
        <Fragment>
          <InfoBlock
            label={t('labels.recipients')}
            value={formData.notificationPreferences.recipientIds && formData.notificationPreferences.recipientIds.length
              ? [...formData.notificationPreferences.recipientIds].map((recipient:IUser) => recipient.name).join(', ')
              : '-'
            }
            direction="row" />
        </Fragment>
      </OutlineBlock>
      <OutlineBlock sx={{ mt: 6 }} label={t('labels.contacts')}>
        <Fragment>
          <InfoBlock
            label=""
            value={formData.contacts && formData.contacts.length
              ? [...formData.contacts].map((contact) => contact.contact?.name).join(', ')
              : '-'
            }
            direction="row" />
        </Fragment>
      </OutlineBlock>
    </Box>
  );
}

export default ReviewCreateStepContent;
